import React from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import theme from '../../theme';
import cnrExpo2023 from '../../assets/images/news/cnrexpo2023.jpg';
import ceoInterview from '../../assets/images/news/boranka_teknoloji_kurucusu_ve_ceosu_emre_okumusasla_vazgecmem_h18716_07ed8.webp';
import aiCommerce from '../../assets/images/news/yapay_zekayi_ticaretin_ayrilmaz_bir_parcasi_haline_getirmeyi_hedefliyor_h16547_53640.webp';

const NewsSection = styled.section`
  padding: 6rem 0 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const NewsCard = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius};
  overflow: hidden;
  box-shadow: ${theme.boxShadow};
  transition: ${theme.transition};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const NewsImage = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  ${NewsCard}:hover & img {
    transform: scale(1.05);
  }
`;

const NewsTag = styled.span`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: ${theme.fontSizes.small};
  font-weight: 500;
`;

const NewsContent = styled.div`
  padding: 1.5rem;
`;

const NewsDate = styled.div`
  color: ${theme.colors.darkGray};
  font-size: ${theme.fontSizes.small};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  
  &:before {
    content: '🗓️';
    margin-right: 0.5rem;
  }
`;

const NewsTitle = styled.h3`
  margin-bottom: 1rem;
  transition: ${theme.transition};
  
  a {
    color: ${theme.colors.secondary};
    
    &:hover {
      color: ${theme.colors.primary};
    }
  }
`;

const NewsExcerpt = styled.p`
  color: ${theme.colors.darkGray};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const ReadMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.primary};
  font-weight: 600;
  
  &:after {
    content: '→';
    margin-left: 0.5rem;
    transition: ${theme.transition};
  }
  
  &:hover:after {
    margin-left: 0.75rem;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 4rem;
`;

const PaginationButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.active ? theme.colors.primary : theme.colors.white};
  color: ${props => props.active ? theme.colors.white : theme.colors.secondary};
  border: 1px solid ${props => props.active ? theme.colors.primary : theme.colors.lightGray};
  border-radius: ${theme.borderRadius};
  margin: 0 0.3rem;
  cursor: pointer;
  transition: ${theme.transition};
  
  &:hover {
    background-color: ${props => props.active ? theme.colors.primary : theme.colors.lightGray};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NewsPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  
  const newsItems = [
    {
      id: 'cnr-expo-2023',
      titleKey: 'news.items.cnrExpo2023.title',
      excerptKey: 'news.items.cnrExpo2023.excerpt',
      image: cnrExpo2023,
      dateKey: 'news.items.cnrExpo2023.date',
      tagKey: 'news.items.cnrExpo2023.tag',
      slug: 'cnr-expo-2023'
    },
    {
      id: 'ceo-interview',
      titleKey: 'news.items.ceoInterview.title',
      excerptKey: 'news.items.ceoInterview.excerpt',
      image: ceoInterview,
      dateKey: 'news.items.ceoInterview.date',
      tagKey: 'news.items.ceoInterview.tag',
      slug: 'boranka-teknoloji-kurucusu-emre-okumus'
    },
    {
      id: 'ai-commerce',
      titleKey: 'news.items.aiCommerce.title',
      excerptKey: 'news.items.aiCommerce.excerpt',
      image: aiCommerce,
      dateKey: 'news.items.aiCommerce.date',
      tagKey: 'news.items.aiCommerce.tag',
      slug: 'yapay-zeka-ticaret'
    }
  ];
  
  return (
    <>
      <SEO 
        titleKey="news.seo.title"
        descriptionKey="news.seo.description"
      />
      
      <NewsSection>
        <div className="container">
          <PageHeader>
            <PageTitle>{t('news.title')}</PageTitle>
          </PageHeader>
          
          <NewsGrid>
            {newsItems.map(news => (
              <NewsCard key={news.id}>
                <NewsImage>
                  <img src={news.image} alt={t(news.titleKey)} loading="lazy" />
                  <NewsTag>{t(news.tagKey)}</NewsTag>
                </NewsImage>
                <NewsContent>
                  <NewsDate>{t(news.dateKey)}</NewsDate>
                  <NewsTitle>
                    <Link to={`/${lang}/news/${news.slug}`}>{t(news.titleKey)}</Link>
                  </NewsTitle>
                  <NewsExcerpt>{t(news.excerptKey)}</NewsExcerpt>
                  <ReadMoreLink to={`/${lang}/news/${news.slug}`}>{t('news.readMore')}</ReadMoreLink>
                </NewsContent>
              </NewsCard>
            ))}
          </NewsGrid>
        </div>
      </NewsSection>
    </>
  );
};

export default NewsPage; 